<template>
	<article class="d-subject-page-custom">
		<PageHero v-bind="{ ...header, breadcrumb }">
			<template #after>
				<!-- Desktop -->
				<div class="d-subject-page-custom__overview <1024:hidden">
					<!-- Tabs -->
					<div class="flex space-x-2" role="tablist">
						<button
							v-for="(item, index) in items"
							:id="`d-subject-page-custom__tab-${item.id}`"
							:key="`tab-${index}`"
							role="tab"
							:aria-selected="
								activeTab === item.id ? 'true' : 'false'
							"
							:aria-controls="`d-subject-page-custom__content-panel-${item.id}`"
							:class="[
								'flex-1 p-sm rounded-t-lg',
								'font-semibold text-body text-left',
								'duration-200 ease-smooth-out',
								'group',

								{
									'bg-white': activeTab === item.id,
									'bg-primary-light': activeTab !== item.id,
								},
							]"
							@click="setActiveTab(item.id)"
						>
							<div
								:class="[
									'flex items-center space-x-xs',
									'duration-200 ease-smooth-out group-hover:pl-8',
								]"
							>
								<div class="w-sm flex-shrink-0">
									<PageIcon
										v-if="item.icon"
										:name="item.icon"
									/>
								</div>
								<span v-text="item.title"></span>
							</div>
						</button>
					</div>

					<!-- Content -->
					<div class="bg-white rounded-b-lg p-lg overflow-hidden">
						<div
							:style="`height: ${contentHeight}px`"
							class="relative duration-200 ease-smooth-out"
						>
							<TransitionGroup
								name="t-subject-page-custom__content"
								@enter="updateContentHeight"
							>
								<div
									v-for="(item, i) in items"
									v-show="item.id === activeTab"
									:id="`d-subject-page-custom__content-panel-${item.id}`"
									:key="`tablist-${i}`"
									:ref="`content-${item.id}`"
									:aria-hidden="item.id !== activeTab"
									:aria-labelledby="`d-subject-page-custom__tab-${item.id}`"
									role="tabpanel"
									class="absolute top-0 left-0 w-full"
								>
									<div class="grid grid-cols-4 gap-md">
										<ContentLink
											v-for="(child, j) in item.children"
											:key="`link-${j}`"
											:to="child.url"
											:target="child.target"
										>
											<template #default>
												{{ child.title }}
											</template>
											<!-- REMOVED BY STO 2022-06-17 -->
											<!--
											<template #description>
												{{ child.teaser }}
											</template>
											-->
										</ContentLink>
									</div>
								</div>
							</TransitionGroup>
						</div>
					</div>
				</div>

				<!-- Mobile -->
				<div class=">=1024:hidden">
					<AccordionLayout
						ref="mobileAccordion"
						:max-one-open="true"
						:min-one-open="true"
						:elements="items"
						:has-caret="false"
						@change="
							({ index, state }) =>
								state && setActiveTab(items[index].id)
						"
					>
						<template #header="{ item }">
							<div class="flex items-center space-x-sm py-8">
								<div class="flex-shrink-0 w-32">
									<PageIcon
										v-if="item.icon"
										:name="item.icon"
									/>
								</div>

								<BaseH2 tag="h2" v-text="item.title" />
							</div>
						</template>

						<template #panel="{ item }">
							<ContentLink
								v-for="(child, index) in item.children"
								:key="`mobile-link-${index}`"
								:to="child.url"
								:target="child.target"
								class="block mb-xs"
							>
								<template #default>
									<span
										class="font-semibold"
										v-text="child.title"
									></span>
								</template>
							</ContentLink>
						</template>
					</AccordionLayout>
				</div>
			</template>
		</PageHero>

		<div class="mx-layout-margin">
			<BlockList
				:block-list="contentElements"
				class="w-full"
				:class="firstElementIsSolid && 'mt-0'"
			/>
		</div>

		<FeedbackForm v-if="activateFeedback" class="mb-lg" />
	</article>
</template>

<script>
import PageHero from '~/components/main/PageHero';
import AccordionLayout from '~/components/shared/AccordionLayout';
import PageIcon from '~/components/shared/PageIcon';
import ContentLink from '~/components/shared/ContentLink';
import BlockList from '~/components/main/BlockList';
import FeedbackForm from '~/components/shared/FeedbackForm';

export default {
	name: 'MainSubjectPageCustom',

	components: {
		PageHero,
		AccordionLayout,
		PageIcon,
		ContentLink,
		BlockList,
		FeedbackForm,
	},

	inheritAttrs: false,

	props: {
		header: {
			type: Object,
			default: () => ({}),
		},
		breadcrumb: {
			type: Array,
			required: false,
		},
		items: {
			type: Array,
			required: false,
		},
		contentElements: {
			type: Array,
			default: () => [],
		},
		activateFeedback: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			activeTab: null,
			contentHeight: 0,
		};
	},

	computed: {
		firstElementIsSolid() {
			return ['BlockSelfService', 'NewsList', 'KultuNaut'].includes(
				this.contentElements?.[0]?.alias || ''
			);
		},
	},

	mounted() {
		const { query } = this.$route;
		this.activeTab = this.items.find((e) => `${e.id}` === query?.subjectid)
			? parseInt(query.subjectid)
			: this.items[0]?.id;

		this.updateContentHeight();
		window.addEventListener('resize', this.updateContentHeight);

		/**
		 * Opens the correct tab on mobile
		 * based on subject id given in
		 * url query.
		 * */
		if (this.$refs?.mobileAccordion) {
			let index = 0;
			this.items.forEach((e, n) => {
				if (e.id === this.activeTab) {
					index = n;
				}
			});

			this.$refs.mobileAccordion.openPanel(index);
		}
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.updateContentHeight);
	},

	methods: {
		setActiveTab(id) {
			this.activeTab = id;

			/**
			 * Update url query to contain
			 * new subject-id.
			 */
			const path = `${window.location}`.split('?')[0];
			const url = `${path}?subjectid=${id}`;
			window.history.replaceState(null, '', url);
		},

		updateContentHeight() {
			if (
				this.$refs[`content-${this.activeTab}`] &&
				this.$refs[`content-${this.activeTab}`][0]
			) {
				const content = this.$refs[`content-${this.activeTab}`][0];
				const { height } = content.getBoundingClientRect();
				this.contentHeight = height;
			}
		},
	},
};
</script>

<style lang="postcss">
.d-subject-page-custom .c-accordion-layout__header--active,
.d-subject-page-custom .c-accordion-layout__panel--active {
	background-color: #fff !important;
}

.t-subject-page-custom__content-enter-active,
.t-subject-page-custom__content-leave-active {
	@apply duration-300 ease-smooth-out transform;
}
.t-subject-page-custom__content-enter,
.t-subject-page-custom__content-leave-to {
	@apply opacity-0;
}
</style>
